/**
 * Axios instance — FastApiRuleEngineAS backend'e istek atar.
 * JWE token iframe bridge üzerinden parent spine app'ten gelir.
 */
import axios from 'axios';
import { getStoredToken, postToParent, requestTokenFromParent } from '../utils/iframe';
import { getStore } from '../store/storeRef';

// NOT: .env'de trailing slash ('https://.../') birakilmis olabilir. Axios
// baseURL + '/rule-engine/...' seklinde concat ederken cift-eglik uretir
// ('https://...//rule-engine/...'). Starlette bazi path'lerde 401/404
// donebilir. Defansif olarak trailing slash'lari kirpiyoruz.
export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8020').replace(/\/+$/, '');

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: false,
});

/**
 * Request interceptor — her istekle JWE token'ı Authorization header'a koyar.
 * Token yoksa (henüz parent'tan gelmemişse) istek yine de gider, backend 401 döner.
 */
apiClient.interceptors.request.use(
  (config) => {
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Correlation id — backend trace için
    config.headers['X-Request-Source'] = 'rule-engine-ui';
    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Response interceptor — 401/403 geldiğinde parent'a auth:error bildir.
 * Backend response format: { success, data, pagination, message } veya
 *                          { success: false, error: { code, message, details } }
 */
apiClient.interceptors.response.use(
  (response) => {
    // Backend standart zarfı açar — data içindekini döner
    const body = response.data;
    if (body && typeof body === 'object' && 'success' in body) {
      if (body.success === false) {
        // Backend success=false ile dönerse error fırlat.
        // NOT: backend payload'unda alan adi `details` (cogul); eski kod
        // `detail` (tekil) okuyordu. Geriye donuk uyumluluk icin ikisini de
        // destekle — yeni kod `err.detail` icine her iki sekli de duser.
        const err = new Error(body.error?.message || 'İşlem başarısız');
        err.code = body.error?.code;
        err.detail = body.error?.details || body.error?.detail;
        err.response = response;
        return Promise.reject(err);
      }
      // Başarılı — data + pagination bilgisini birlikte döndür
      return {
        ...response,
        data: body.data,
        pagination: body.pagination,
        message: body.message,
      };
    }
    return response;
  },
  async (error) => {
    if (!error.response) {
      // Network / timeout
      postToParent('error', {
        kind: 'network',
        message: error.message,
      });
      return Promise.reject(error);
    }

    const status = error.response.status;
    const body = error.response.data || {};
    const apiError = body.error || {};

    if (status === 401) {
      // Kök neden (müşteri "Sıralama Excel'i inmiyor", 2026-05): iframe token'ı
      // export anında sessionStorage'da olmayabiliyor (propagation boşluğu /
      // önceki 401 sonrası temizlenme) → backend "Authorization header eksik".
      // Eskiden 401'de doğrudan logout ediyorduk; bu "oturum düştü" yanılgısı
      // yaratıyordu. Yeni davranış: önce parent'tan token tazele + isteği BİR
      // KEZ retry et; sadece refresh başarısızsa (timeout/parent yok) logout.
      const original = error.config || {};
      if (!original._retry401) {
        original._retry401 = true;
        try {
          const fresh = await requestTokenFromParent();
          if (fresh) {
            original.headers = original.headers || {};
            original.headers.Authorization = `Bearer ${fresh}`;
            // Retry — başarılı response success interceptor'dan geçer (zarf açılır).
            return apiClient(original);
          }
        } catch (refreshErr) {
          // Refresh başarısız — aşağıdaki logout'a düş.
        }
      }
      // Refresh başarısız ya da retry sonrası yine 401 → logout:
      // 1. Parent spine app'e bildir. 2. Redux logout. 3. DOM event.
      postToParent('auth:expired', { status });
      getStore()?.dispatch({ type: 'auth/logout' });
      window.dispatchEvent(new CustomEvent('re:auth:401'));
    } else if (status === 403) {
      postToParent('auth:forbidden', { code: apiError.code });
    }

    const err = new Error(apiError.message || error.message || 'Sunucu hatası');
    err.status = status;
    err.code = apiError.code;
    // Geriye donuk uyumluluk: backend `details` (cogul) yazsa da, eski yerlerde
    // `detail` (tekil) varsa onu da kabul et.
    err.detail = apiError.details || apiError.detail;
    err.response = error.response;
    return Promise.reject(err);
  },
);

export default apiClient;
